// Sanitizador de PII antes de mandar nada a observability. Crítico porque
// Vortigon maneja datos de víctimas / denunciantes / coordenadas — no podemos
// loguear nombres ni direcciones a un servicio externo.
//
// Estrategia:
//  1. Recorrer el object recursivo. Si la KEY matchea la lista sensible,
//     reemplazar el VALOR completo por "[REDACTED]". Funciona en cualquier nivel.
//  2. Si la key es neutra pero el valor es string, correr regex de patrones
//     conocidos (DNI argentino, coordenadas decimales sueltas en frases) y
//     reemplazar match por placeholder.
//  3. Profundidad máxima 8 (evitar loops y blowups).
//  4. Strings > 2000 chars se truncan (probablemente notas/dump completo).
//
// Las KEYS se chequean case-insensitive y permiten matches parciales — `dni`
// matchea `dni`, `victimDni`, `dniNumber`. Esto puede tener falsos positivos
// (ej. `agencyName` no es PII pero matchea `name`). Si encontrás uno, agregalo
// a `KEY_ALLOWLIST` con su path exacto.

const REDACTED = "[REDACTED]";
const MAX_DEPTH = 8;
const MAX_STRING_LENGTH = 2000;

// Substrings que, si aparecen en una key (lowercase), redactan el valor.
const SENSITIVE_KEY_FRAGMENTS = [
  // Credenciales
  "password",
  "passwd",
  "secret",
  "token",
  "apikey",
  "accesskey",
  "authorization",
  "cookie",
  "sessionid",

  // Identificación personal
  "dni",
  "documento",
  "document",
  "passport",
  "ssn",
  "cuit",
  "cuil",

  // Contacto
  "phone",
  "telefono",
  "celular",
  "mobile",

  // Nombres de personas (no de agencies/services)
  "fullname",
  "firstname",
  "lastname",
  "victimname",
  "reportername",
  "personname",
  "callername",

  // Ubicación PII
  "address",
  "direccion",
  "streetaddress",
  "locationtext",
  "latitude",
  "longitude",
  "coordinates",

  // Contenido libre que suele tener PII en Vortigon
  "notes",
  "operatornotes",
  "report.notes",
  "report.summary",
];

// Keys exactas que NUNCA se redactan aunque matcheen un fragmento sensible
// (caso "name" en service/agency/event names).
const KEY_ALLOWLIST = new Set<string>([
  "name", // ambiguo — caller debe usar agencyName si quiere distinguir
  "servicename",
  "agencyname",
  "eventname",
  "actionname",
  "rolename",
  "tagname",
]);

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (KEY_ALLOWLIST.has(lower)) return false;
  return SENSITIVE_KEY_FRAGMENTS.some((frag) => lower.includes(frag));
}

// Regex de patterns dentro de valores string libres.
const DNI_REGEX = /\b\d{1,2}\.?\d{3}\.?\d{3}\b/g;
const COORD_REGEX = /(-?\d{1,3}\.\d{4,})\s*,\s*(-?\d{1,3}\.\d{4,})/g;

function scrubString(value: string): string {
  let out = value;
  if (out.length > MAX_STRING_LENGTH) {
    out = `${out.slice(0, MAX_STRING_LENGTH)}…[truncated]`;
  }
  out = out.replace(DNI_REGEX, "[DNI]");
  out = out.replace(COORD_REGEX, "[COORDS]");
  return out;
}

export function scrubPII<T>(input: T): T {
  return scrubValue(input, 0) as T;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (depth > MAX_DEPTH) return "[MAX_DEPTH]";

  if (typeof value === "string") return scrubString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "bigint") return value.toString();

  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1));
  }

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) {
    return {
      name: value.name,
      message: scrubString(value.message),
      // No incluimos stack — Sentry/PostHog ya lo capturan por separado.
    };
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (isSensitiveKey(key)) {
        out[key] = REDACTED;
      } else {
        out[key] = scrubValue(v, depth + 1);
      }
    }
    return out;
  }

  return value;
}
